import React, { useState, useEffect, useCallback } from "react";
import { customFetch } from '../apiClient';

const DischargeAdvanceModal = ({ show, ipdId, onClose }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const fetchRecords = useCallback(async () => {
    if (!ipdId) return;
    setLoading(true);
   try {
      const res = await customFetch(`/IpdAdvDt/search-by-ipd?ipdId=${ipdId}`);
      if (!res.ok) throw new Error("Failed to fetch records");
      const data = await res.json();
      setRecords(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [ipdId]);

  useEffect(() => {
    if (show) fetchRecords();
  }, [show, fetchRecords]);

  // ... (Editing Logic same as IPDAdvanceTable) ...
  const startEditing = (record) => {
    setEditId(record.advID);
    setEditData({
      adv_Amt: record.adv_Amt || "",
      remarks: record.remarks || "",
      payMode: record.payMode || "Cash",
      chqNo: record.chqNo || "",
      chqDate: record.chqDate ? record.chqDate.substring(0, 10) : "",
      recvDate: record.recvDate ? record.recvDate.substring(0, 10) : "",
    });
  };

  const handleChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const saveRecord = async () => {
    if (!editId) return;
   try {
      const originalRecord = records.find(r => r.advID === editId);
      const updatedRecord = { ...originalRecord, ...editData, adv_Amt: parseFloat(editData.adv_Amt) };

      const res = await customFetch(`/IpdAdvDt/${editId}`, {
        method: "PUT",
        body: JSON.stringify(updatedRecord),
      });

      if (res.ok) {
        setMessage("Updated successfully");
        setEditId(null);
        fetchRecords(); // Refresh list
      } else {
        throw new Error("Failed to update");
      }
    } catch (e) {
      setError(e.message);
    }
  };

  const deleteRecord = async (id) => {
    if (!window.confirm("Delete this advance?")) return;
   try {
      const res = await customFetch(`/IpdAdvDt/${id}`, {
        method: "DELETE",
      });
      if (res.ok) {
        setMessage("Deleted successfully");
        fetchRecords();
      }
    } catch (e) {
      setError(e.message);
    }
  };

  if (!show) return null;

  return (
    <div className="nested-modal-overlay">
      <div className="nested-modal-container">
        <div className="nested-modal-header">
          <h5>Manage Advance Payments</h5>
          <button className="btn-close" onClick={onClose}><i className="bi bi-x-lg"></i></button>
        </div>
        <div className="nested-modal-body">
          {error && <div className="alert alert-danger">{error}</div>}
          {message && <div className="alert alert-success">{message}</div>}
          
          <div className="table-responsive">
            <table className="table table-bordered table-sm">
              <thead className="table-light">
                <tr>
                  <th>Amount</th>
                  <th>Date</th>
                  <th>Mode</th>
                  <th>Remarks</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? <tr><td colSpan="5" className="text-center">Loading...</td></tr> : 
                 records.length === 0 ? <tr><td colSpan="5" className="text-center">No records found</td></tr> :
                 records.map(r => (
                  <tr key={r.advID}>
                    <td>
                      {editId === r.advID ? 
                        <input type="number" className="form-control form-control-sm" value={editData.adv_Amt} onChange={e => handleChange('adv_Amt', e.target.value)} /> : 
                        `₹${r.adv_Amt}`}
                    </td>
                    <td>{editId === r.advID ? <input type="date" className="form-control form-control-sm" value={editData.recvDate} onChange={e => handleChange('recvDate', e.target.value)}/> : r.recvDate?.split('T')[0]}</td>
                    <td>{editId === r.advID ? 
                        <select className="form-select form-select-sm" value={editData.payMode} onChange={e => handleChange('payMode', e.target.value)}>
                            <option value="Cash">Cash</option><option value="UPI">UPI</option><option value="Card">Card</option>
                        </select> : r.payMode}
                    </td>
                    <td>{editId === r.advID ? <input type="text" className="form-control form-control-sm" value={editData.remarks} onChange={e => handleChange('remarks', e.target.value)}/> : r.remarks}</td>
                    <td>
                      {editId === r.advID ? (
                        <>
                          <button className="btn btn-sm btn-success me-1" onClick={saveRecord}><i className="bi bi-check"></i></button>
                          <button className="btn btn-sm btn-secondary" onClick={() => setEditId(null)}><i className="bi bi-x"></i></button>
                        </>
                      ) : (
                        <>
                          <button className="btn btn-sm btn-primary me-1" onClick={() => startEditing(r)}><i className="bi bi-pencil"></i></button>
                          <button className="btn btn-sm btn-danger" onClick={() => deleteRecord(r.advID)}><i className="bi bi-trash"></i></button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      
      <style jsx>{`
        .nested-modal-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.6);
          z-index: 10000; /* Higher than parent modal */
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .nested-modal-container {
          background: white;
          width: 90%;
          max-width: 800px;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 5px 15px rgba(0,0,0,0.3);
        }
        .nested-modal-header {
          background: #f8f9fa;
          padding: 10px 15px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid #ddd;
        }
        .nested-modal-body {
          padding: 15px;
          max-height: 70vh;
          overflow-y: auto;
        }
        .btn-close { background: none; border: none; font-size: 1.2rem; }
      `}</style>
    </div>
  );
};

export default DischargeAdvanceModal;